import { useState } from 'react';

interface Influencer {
  name: string;
  avatar: string;
  followers: string;
  sales: string;
}

interface Brand {
  id: number;
  name: string;
  positioning: string;
  description: string;
  logo: string;
  priceRange: string;
  coreFeatures: string[];
  sales: string;
  salesDate: string;
  rating: number;
  platforms: {
    tmall: { rating: string; qrCode: string };
    jd: { rating: string; qrCode: string };
    douyin: { rating: string; qrCode: string };
    xiaohongshu: { qrCode: string };
  };
  influencers: {
    douyin: Influencer;
    taobao: Influencer;
    kuaishou: Influencer;
    xiaohongshu: Influencer;
  };
}

export default function BrandSection() {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [modalLayout, setModalLayout] = useState<'default' | 'influencers-first' | 'qr-first'>('default');

  const brands: Brand[] = [
    {
      id: 1,
      name: '滇韵古茶',
      positioning: '高端收藏级普洱',
      description: '传承百年工艺，精选古树茶叶，每一片都承载着岁月的沉淀与自然的馈赠',
      logo: 'https://public.readdy.ai/ai/img_res/2d1ad349b00d7828ada39bfb67f498dd.jpg',
      priceRange: '¥398-2888',
      coreFeatures: ['古树原料', '传统工艺', '陈化5年+', '收藏价值'],
      sales: '月销28.6万件',
      salesDate: '2024年12月',
      rating: 4.9,
      platforms: {
        tmall: {
          rating: '98.5%',
          qrCode: 'https://readdy.ai/api/search-image?query=Tmall%20store%20QR%20code%20for%20premium%20tea%20brand%2C%20clean%20white%20background%2C%20red%20Tmall%20colors%2C%20professional%20e-commerce%20design%2C%20square%20format%20with%20brand%20elements&width=120&height=120&seq=tmall-qr-1&orientation=squarish'
        },
        jd: {
          rating: '98.8%',
          qrCode: 'https://readdy.ai/api/search-image?query=JD.com%20store%20QR%20code%20for%20tea%20business%2C%20white%20background%2C%20red%20JD%20colors%2C%20professional%20e-commerce%20design%2C%20square%20format%20with%20shopping%20elements&width=120&height=120&seq=jd-qr-1&orientation=squarish'
        },
        douyin: {
          rating: '99.2%',
          qrCode: 'https://readdy.ai/api/search-image?query=Douyin%20TikTok%20store%20QR%20code%20for%20tea%20brand%2C%20white%20background%2C%20black%20and%20red%20colors%2C%20modern%20social%20commerce%20design%2C%20square%20format&width=120&height=120&seq=douyin-qr-1&orientation=squarish'
        },
        xiaohongshu: {
          qrCode: 'https://readdy.ai/api/search-image?query=Xiaohongshu%20Little%20Red%20Book%20store%20QR%20code%2C%20white%20background%2C%20red%20colors%2C%20lifestyle%20social%20commerce%20design%2C%20square%20format&width=120&height=120&seq=xhs-qr-1&orientation=squarish'
        }
      },
      influencers: {
        douyin: {
          name: '茶文化传承者',
          avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20tea%20master%20influencer%2C%20elegant%20traditional%20clothing%2C%20tea%20ceremony%20setting%2C%20warm%20lighting%2C%20authentic%20tea%20culture%20atmosphere%2C%20sophisticated%20portrait%20photography%2C%20cultural%20heritage%20aesthetic&width=60&height=60&seq=douyin-inf-1&orientation=squarish',
          followers: '520万粉丝',
          sales: '月带货1200万'
        },
        taobao: {
          name: '古茶收藏家',
          avatar: 'https://readdy.ai/api/search-image?query=Experienced%20tea%20collector%20and%20seller%2C%20professional%20studio%20setting%2C%20premium%20tea%20collection%20background%2C%20knowledgeable%20expert%20appearance%2C%20trustworthy%20e-commerce%20presenter%2C%20high-quality%20portrait&width=60&height=60&seq=taobao-inf-1&orientation=squarish',
          followers: '180万粉丝',
          sales: '月销1800万'
        },
        kuaishou: {
          name: '老茶客阿强',
          avatar: 'https://readdy.ai/api/search-image?query=Friendly%20middle-aged%20tea%20enthusiast%2C%20casual%20approachable%20style%2C%20authentic%20tea%20lover%2C%20down-to-earth%20personality%2C%20genuine%20tea%20culture%20sharing%2C%20natural%20portrait%20photography&width=60&height=60&seq=kuaishou-inf-1&orientation=squarish',
          followers: '380万粉丝',
          sales: '月带货800万'
        },
        xiaohongshu: {
          name: '茶韵小姐姐',
          avatar: 'https://readdy.ai/api/search-image?query=Young%20stylish%20female%20tea%20lifestyle%20blogger%2C%20modern%20aesthetic%2C%20elegant%20tea%20ceremony%20setup%2C%20trendy%20casual%20outfit%2C%20lifestyle%20influencer%20style%2C%20contemporary%20portrait%20photography&width=60&height=60&seq=xhs-inf-1&orientation=squarish',
          followers: '290万粉丝',
          sales: '月推广600万'
        }
      }
    },
    {
      id: 2,
      name: '云境茶香',
      positioning: '轻奢日常茶饮',
      description: '现代工艺与传统茶艺的完美融合，为都市白领打造的轻奢茶饮体验',
      logo: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20tea%20brand%20logo%20with%20cloud%20and%20mountain%20elements%2C%20blue%20and%20white%20colors%2C%20contemporary%20style%2C%20clean%20geometric%20design%20for%20urban%20tea%20consumption&width=80&height=80&seq=yunjing-logo&orientation=squarish',
      priceRange: '¥158-688',
      coreFeatures: ['现代包装', '便携设计', '办公室茶', '年轻时尚'],
      sales: '月销42.1万件',
      salesDate: '2024年12月',
      rating: 4.8,
      platforms: {
        tmall: {
          rating: '97.8%',
          qrCode: 'https://readdy.ai/api/search-image?query=Tmall%20store%20QR%20code%20for%20modern%20tea%20brand%2C%20clean%20white%20background%2C%20red%20Tmall%20colors%2C%20contemporary%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=tmall-qr-2&orientation=squarish'
        },
        jd: {
          rating: '98.1%',
          qrCode: 'https://readdy.ai/api/search-image?query=JD.com%20store%20QR%20code%20for%20lifestyle%20tea%20business%2C%20white%20background%2C%20red%20JD%20colors%2C%20modern%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=jd-qr-2&orientation=squarish'
        },
        douyin: {
          rating: '98.9%',
          qrCode: 'https://readdy.ai/api/search-image?query=Douyin%20TikTok%20store%20QR%20code%20for%20modern%20tea%20brand%2C%20white%20background%2C%20black%20and%20red%20colors%2C%20trendy%20social%20commerce%20design%2C%20square%20format&width=120&height=120&seq=douyin-qr-2&orientation=squarish'
        },
        xiaohongshu: {
          qrCode: 'https://readdy.ai/api/search-image?query=Xiaohongshu%20store%20QR%20code%20for%20trendy%20tea%20brand%2C%20white%20background%2C%20red%20colors%2C%20young%20lifestyle%20design%2C%20square%20format&width=120&height=120&seq=xhs-qr-2&orientation=squarish'
        }
      },
      influencers: {
        douyin: {
          name: '都市茶生活',
          avatar: 'https://readdy.ai/api/search-image?query=Modern%20urban%20lifestyle%20influencer%20with%20tea%2C%20professional%20office%20setting%2C%20contemporary%20style%2C%20business%20casual%20attire%2C%20modern%20tea%20culture%20presentation%2C%20polished%20portrait&width=60&height=60&seq=douyin-inf-2&orientation=squarish',
          followers: '680万粉丝',
          sales: '月带货2100万'
        },
        taobao: {
          name: '轻奢茶品专家',
          avatar: 'https://readdy.ai/api/search-image?query=Sophisticated%20tea%20lifestyle%20expert%2C%20premium%20modern%20setting%2C%20elegant%20professional%20appearance%2C%20luxury%20tea%20presentation%2C%20high-end%20e-commerce%20style%2C%20refined%20portrait%20photography&width=60&height=60&seq=taobao-inf-2&orientation=squarish',
          followers: '220万粉丝',
          sales: '月销2000万'
        },
        kuaishou: {
          name: '办公室小白',
          avatar: 'https://readdy.ai/api/search-image?query=Young%20office%20worker%20tea%20enthusiast%2C%20friendly%20approachable%20style%2C%20office%20environment%2C%20casual%20professional%20look%2C%20relatable%20personality%2C%20authentic%20workplace%20tea%20culture&width=60&height=60&seq=kuaishou-inf-2&orientation=squarish',
          followers: '450万粉丝',
          sales: '月带货1500万'
        },
        xiaohongshu: {
          name: '茶香生活家',
          avatar: 'https://readdy.ai/api/search-image?query=Trendy%20lifestyle%20blogger%20specializing%20in%20tea%20culture%2C%20modern%20minimalist%20aesthetic%2C%20stylish%20home%20setting%2C%20contemporary%20tea%20ceremony%2C%20Instagram-worthy%20presentation&width=60&height=60&seq=xhs-inf-2&orientation=squarish',
          followers: '420万粉丝',
          sales: '月推广1200万'
        }
      }
    },
    {
      id: 3,
      name: '普洱传奇',
      positioning: '经典传承普洱',
      description: '秉承传统制茶工艺，每一饼茶都是匠心之作，传承普洱茶的经典韵味',
      logo: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20tea%20brand%20logo%20with%20pu-er%20tea%20cake%20elements%2C%20brown%20and%20gold%20colors%2C%20classic%20heritage%20style%2C%20vintage%20design%20with%20traditional%20patterns&width=80&height=80&seq=puer-logo&orientation=squarish',
      priceRange: '¥268-1288',
      coreFeatures: ['传统工艺', '经典口感', '性价比高', '稳定品质'],
      sales: '月销35.8万件',
      salesDate: '2024年12月',
      rating: 4.7,
      platforms: {
        tmall: {
          rating: '97.2%',
          qrCode: 'https://readdy.ai/api/search-image?query=Tmall%20store%20QR%20code%20for%20traditional%20tea%20brand%2C%20clean%20white%20background%2C%20red%20Tmall%20colors%2C%20classic%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=tmall-qr-3&orientation=squarish'
        },
        jd: {
          rating: '97.9%',
          qrCode: 'https://readdy.ai/api/search-image?query=JD.com%20store%20QR%20code%20for%20heritage%20tea%20business%2C%20white%20background%2C%20red%20JD%20colors%2C%20traditional%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=jd-qr-3&orientation=squarish'
        },
        douyin: {
          rating: '98.4%',
          qrCode: 'https://readdy.ai/api/search-image?query=Douyin%20TikTok%20store%20QR%20code%20for%20classic%20tea%20brand%2C%20white%20background%2C%20black%20and%20red%20colors%2C%20traditional%20social%20commerce%20design%2C%20square%20format&width=120&height=120&seq=douyin-qr-3&orientation=squarish'
        },
        xiaohongshu: {
          qrCode: 'https://readdy.ai/api/search-image?query=Xiaohongshu%20store%20QR%20code%20for%20heritage%20tea%20brand%2C%20white%20background%2C%20red%20colors%2C%20cultural%20lifestyle%20design%2C%20square%20format&width=120&height=120&seq=xhs-qr-3&orientation=squarish'
        }
      },
      influencers: {
        douyin: {
          name: '传统茶艺师',
          avatar: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20tea%20master%20in%20classic%20attire%2C%20traditional%20tea%20ceremony%20setting%2C%20cultural%20heritage%20atmosphere%2C%20authentic%20tea%20preparation%2C%20classical%20portrait%20style&width=60&height=60&seq=douyin-inf-3&orientation=squarish',
          followers: '320万粉丝',
          sales: '月带货900万'
        },
        taobao: {
          name: '经典普洱专营',
          avatar: 'https://readdy.ai/api/search-image?query=Experienced%20Pu-erh%20tea%20merchant%2C%20traditional%20tea%20shop%20background%2C%20authentic%20tea%20seller%20appearance%2C%20professional%20tea%20business%20presentation%2C%20trustworthy%20expert%20image&width=60&height=60&seq=taobao-inf-3&orientation=squarish',
          followers: '150万粉丝',
          sales: '月销1600万'
        },
        kuaishou: {
          name: '老茶友老王',
          avatar: 'https://readdy.ai/api/search-image?query=Genuine%20tea%20enthusiast%2C%20middle-aged%20friendly%20appearance%2C%20authentic%20tea%20lover%20personality%2C%20traditional%20tea%20culture%20sharing%2C%20down-to-earth%20approachable%20style&width=60&height=60&seq=kuaishou-inf-3&orientation=squarish',
          followers: '280万粉丝',
          sales: '月带货700万'
        },
        xiaohongshu: {
          name: '茶文化研习生',
          avatar: 'https://readdy.ai/api/search-image?query=Young%20tea%20culture%20student%20and%20blogger%2C%20educational%20content%20creator%2C%20modern%20approach%20to%20traditional%20tea%2C%20academic%20tea%20study%20aesthetic%2C%20contemporary%20cultural%20preservation&width=60&height=60&seq=xhs-inf-3&orientation=squarish',
          followers: '180万粉丝',
          sales: '月推广400万'
        }
      }
    },
    {
      id: 4,
      name: '茶马古道',
      positioning: '文化主题茶品',
      description: '重走茶马古道，品味历史文化，每一口茶都诉说着古道上的传奇故事',
      logo: 'https://readdy.ai/api/search-image?query=Cultural%20tea%20brand%20logo%20with%20ancient%20tea%20horse%20road%20elements%2C%20earthy%20brown%20and%20gold%20colors%2C%20historical%20heritage%20style%2C%20map%20and%20caravan%20design%20elements&width=80&height=80&seq=chama-logo&orientation=squarish',
      priceRange: '¥188-988',
      coreFeatures: ['文化内涵', '故事包装', '礼品首选', '历史传承'],
      sales: '月销19.2万件',
      salesDate: '2024年12月',
      rating: 4.8,
      platforms: {
        tmall: {
          rating: '98.1%',
          qrCode: 'https://readdy.ai/api/search-image?query=Tmall%20store%20QR%20code%20for%20cultural%20tea%20brand%2C%20clean%20white%20background%2C%20red%20Tmall%20colors%2C%20heritage%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=tmall-qr-4&orientation=squarish'
        },
        jd: {
          rating: '98.3%',
          qrCode: 'https://readdy.ai/api/search-image?query=JD.com%20store%20QR%20code%20for%20cultural%20tea%20business%2C%20white%20background%2C%20red%20JD%20colors%2C%20traditional%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=jd-qr-4&orientation=squarish'
        },
        douyin: {
          rating: '99.1%',
          qrCode: 'https://readdy.ai/api/search-image?query=Douyin%20TikTok%20store%20QR%20code%20for%20heritage%20tea%20brand%2C%20white%20background%2C%20black%20and%20red%20colors%2C%20cultural%20social%20commerce%20design%2C%20square%20format&width=120&height=120&seq=douyin-qr-4&orientation=squarish'
        },
        xiaohongshu: {
          qrCode: 'https://readdy.ai/api/search-image?query=Xiaohongshu%20store%20QR%20code%20for%20cultural%20tea%20brand%2C%20white%20background%2C%20red%20colors%2C%20heritage%20lifestyle%20design%2C%20square%20format&width=120&height=120&seq=xhs-qr-4&orientation=squarish'
        }
      },
      influencers: {
        douyin: {
          name: '古道寻茶人',
          avatar: 'https://readdy.ai/api/search-image?query=Adventure%20travel%20blogger%20specializing%20in%20tea%20culture%20exploration%2C%20outdoor%20explorer%20appearance%2C%20cultural%20discovery%20theme%2C%20authentic%20journey%20aesthetic%2C%20historical%20tea%20route%20exploration&width=60&height=60&seq=douyin-inf-4&orientation=squarish',
          followers: '430万粉丝',
          sales: '月带货1100万'
        },
        taobao: {
          name: '文化茶品馆',
          avatar: 'https://readdy.ai/api/search-image?query=Cultural%20tea%20curator%20and%20seller%2C%20traditional%20Chinese%20scholar%20appearance%2C%20cultural%20heritage%20setting%2C%20antique%20tea%20collection%20background%2C%20educated%20tea%20merchant%20presentation&width=60&height=60&seq=taobao-inf-4&orientation=squarish',
          followers: '120万粉丝',
          sales: '月销1200万'
        },
        kuaishou: {
          name: '古道老司机',
          avatar: 'https://readdy.ai/api/search-image?query=Experienced%20tea%20route%20traveler%2C%20rugged%20outdoor%20appearance%2C%20authentic%20adventure%20style%2C%20tea%20culture%20explorer%2C%20genuine%20storyteller%20personality%2C%20weathered%20experienced%20look&width=60&height=60&seq=kuaishou-inf-4&orientation=squarish',
          followers: '350万粉丝',
          sales: '月带货600万'
        },
        xiaohongshu: {
          name: '历史茶韵',
          avatar: 'https://readdy.ai/api/search-image?query=Young%20female%20history%20enthusiast%20and%20tea%20blogger%2C%20cultural%20education%20content%20creator%2C%20modern%20approach%20to%20historical%20tea%20culture%2C%20academic%20aesthetic%20with%20lifestyle%20elements&width=60&height=60&seq=xhs-inf-4&orientation=squarish',
          followers: '260万粉丝',
          sales: '月推广500万'
        }
      }
    },
    {
      id: 5,
      name: '古树秘境',
      positioning: '野生古树茶',
      description: '深入云南原始森林，寻找百年古茶树，每一片茶叶都是大自然的珍贵馈赠',
      logo: 'https://readdy.ai/api/search-image?query=Wild%20ancient%20tree%20tea%20brand%20logo%20with%20mystical%20forest%20elements%2C%20deep%20green%20and%20gold%20colors%2C%20natural%20organic%20style%2C%20ancient%20tree%20silhouette%20design&width=80&height=80&seq=gushu-logo&orientation=squarish',
      priceRange: '¥588-3888',
      coreFeatures: ['野生原料', '古树茶王', '限量发售', '稀有珍品'],
      sales: '月销8.9万件',
      salesDate: '2024年12月',
      rating: 4.9,
      platforms: {
        tmall: {
          rating: '99.1%',
          qrCode: 'https://readdy.ai/api/search-image?query=Tmall%20store%20QR%20code%20for%20premium%20wild%20tea%20brand%2C%20clean%20white%20background%2C%20red%20Tmall%20colors%2C%20luxury%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=tmall-qr-5&orientation=squarish'
        },
        jd: {
          rating: '99.3%',
          qrCode: 'https://readdy.ai/api/search-image?query=JD.com%20store%20QR%20code%20for%20luxury%20tea%20business%2C%20white%20background%2C%20red%20JD%20colors%2C%20premium%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=jd-qr-5&orientation=squarish'
        },
        douyin: {
          rating: '99.5%',
          qrCode: 'https://readdy.ai/api/search-image?query=Douyin%20TikTok%20store%20QR%20code%20for%20premium%20tea%20brand%2C%20white%20background%2C%20black%20and%20red%20colors%2C%20luxury%20social%20commerce%20design%2C%20square%20format&width=120&height=120&seq=douyin-qr-5&orientation=squarish'
        },
        xiaohongshu: {
          qrCode: 'https://readdy.ai/api/search-image?query=Xiaohongshu%20store%20QR%20code%20for%20luxury%20tea%20brand%2C%20white%20background%2C%20red%20colors%2C%20premium%20lifestyle%20design%2C%20square%20format&width=120&height=120&seq=xhs-qr-5&orientation=squarish'
        }
      },
      influencers: {
        douyin: {
          name: '野茶探险家',
          avatar: 'https://readdy.ai/api/search-image?query=Professional%20wild%20tea%20explorer%20in%20forest%20setting%2C%20outdoor%20adventure%20gear%2C%20natural%20environment%20background%2C%20authentic%20tea%20hunter%20appearance%2C%20wilderness%20exploration%20aesthetic&width=60&height=60&seq=douyin-inf-5&orientation=squarish',
          followers: '280万粉丝',
          sales: '月带货800万'
        },
        taobao: {
          name: '古树茶珍藏',
          avatar: 'https://readdy.ai/api/search-image?query=Premium%20tea%20collector%20specializing%20in%20ancient%20tree%20teas%2C%20sophisticated%20connoisseur%20appearance%2C%20luxury%20tea%20collection%20setting%2C%20exclusive%20tea%20merchant%20presentation&width=60&height=60&seq=taobao-inf-5&orientation=squarish',
          followers: '90万粉丝',
          sales: '月销1500万'
        },
        kuaishou: {
          name: '山里采茶人',
          avatar: 'https://readdy.ai/api/search-image?query=Local%20tea%20farmer%20and%20picker%20from%20mountain%20regions%2C%20authentic%20rural%20appearance%2C%20traditional%20tea%20cultivation%20setting%2C%20genuine%20tea%20growing%20expertise%2C%20honest%20farmer%20presentation&width=60&height=60&seq=kuaishou-inf-5&orientation=squarish',
          followers: '200万粉丝',
          sales: '月带货500万'
        },
        xiaohongshu: {
          name: '自然茶语',
          avatar: 'https://readdy.ai/api/search-image?query=Nature-loving%20female%20tea%20blogger%2C%20eco-friendly%20lifestyle%20aesthetic%2C%20natural%20outdoor%20setting%2C%20sustainable%20tea%20culture%20presentation%2C%20environmental%20consciousness%20theme&width=60&height=60&seq=xhs-inf-5&orientation=squarish',
          followers: '150万粉丝',
          sales: '月推广300万'
        }
      }
    },
    {
      id: 6,
      name: '悦享茶时',
      positioning: '年轻化茶饮',
      description: '为新生代茶友量身定制，融合现代生活方式，让喝茶成为一种时尚享受',
      logo: 'https://readdy.ai/api/search-image?query=Young%20modern%20tea%20brand%20logo%20with%20trendy%20elements%2C%20bright%20colors%20and%20gradients%2C%20contemporary%20style%2C%20youthful%20and%20energetic%20design%20for%20new%20generation%20tea%20drinkers&width=80&height=80&seq=yuexiang-logo&orientation=squarish',
      priceRange: '¥88-388',
      coreFeatures: ['年轻时尚', '创新口味', '社交属性', '网红爆款'],
      sales: '月销67.3万件',
      salesDate: '2024年12月',
      rating: 4.6,
      platforms: {
        tmall: {
          rating: '96.8%',
          qrCode: 'https://readdy.ai/api/search-image?query=Tmall%20store%20QR%20code%20for%20trendy%20tea%20brand%2C%20clean%20white%20background%2C%20red%20Tmall%20colors%2C%20youthful%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=tmall-qr-6&orientation=squarish'
        },
        jd: {
          rating: '97.4%',
          qrCode: 'https://readdy.ai/api/search-image?query=JD.com%20store%20QR%20code%20for%20young%20tea%20business%2C%20white%20background%2C%20red%20JD%20colors%2C%20trendy%20e-commerce%20design%2C%20square%20format&width=120&height=120&seq=jd-qr-6&orientation=squarish'
        },
        douyin: {
          rating: '98.7%',
          qrCode: 'https://readdy.ai/api/search-image?query=Douyin%20TikTok%20store%20QR%20code%20for%20trendy%20tea%20brand%2C%20white%20background%2C%20black%20and%20red%20colors%2C%20youthful%20social%20commerce%20design%2C%20square%20format&width=120&height=120&seq=douyin-qr-6&orientation=squarish'
        },
        xiaohongshu: {
          qrCode: 'https://readdy.ai/api/search-image?query=Xiaohongshu%20store%20QR%20code%20for%20trendy%20tea%20brand%2C%20white%20background%2C%20red%20colors%2C%20youthful%20lifestyle%20design%2C%20square%20format&width=120&height=120&seq=xhs-qr-6&orientation=squarish'
        }
      },
      influencers: {
        douyin: {
          name: '茶饮时尚达人',
          avatar: 'https://readdy.ai/api/search-image?query=Young%20trendy%20tea%20lifestyle%20influencer%2C%20fashionable%20appearance%2C%20modern%20tea%20culture%20presentation%2C%20stylish%20casual%20outfit%2C%20contemporary%20social%20media%20aesthetic&width=60&height=60&seq=douyin-inf-6&orientation=squarish',
          followers: '800万粉丝',
          sales: '月带货2500万'
        },
        taobao: {
          name: '年轻茶生活',
          avatar: 'https://readdy.ai/api/search-image?query=Young%20professional%20tea%20seller%20targeting%20millennial%20market%2C%20modern%20e-commerce%20presentation%2C%20trendy%20casual%20style%2C%20youth-oriented%20tea%20business%20approach&width=60&height=60&seq=taobao-inf-6&orientation=squarish',
          followers: '300万粉丝',
          sales: '月销2200万'
        },
        kuaishou: {
          name: '95后茶友',
          avatar: 'https://readdy.ai/api/search-image?query=Young%20Generation%20Z%20tea%20enthusiast%2C%20casual%20trendy%20appearance%2C%20authentic%20young%20person%20style%2C%20relatable%20millennial%20tea%20culture%2C%20friendly%20approachable%20personality&width=60&height=60&seq=kuaishou-inf-6&orientation=squarish',
          followers: '600万粉丝',
          sales: '月带货1800万'
        },
        xiaohongshu: {
          name: '潮流茶女孩',
          avatar: 'https://readdy.ai/api/search-image?query=Trendy%20young%20female%20tea%20blogger%2C%20Instagram-worthy%20aesthetic%2C%20modern%20lifestyle%20presentation%2C%20fashionable%20tea%20culture%20content%2C%20stylish%20contemporary%20look&width=60&height=60&seq=xhs-inf-6&orientation=squarish',
          followers: '550万粉丝',
          sales: '月推广1600万'
        }
      }
    }
  ];

  const platformIcons = {
    tmall: 'ri-shopping-bag-line',
    jd: 'ri-shopping-cart-line',
    douyin: 'ri-music-line',
    xiaohongshu: 'ri-book-open-line'
  };

  const platformNames = {
    tmall: '天猫',
    jd: '京东',
    douyin: '抖音',
    xiaohongshu: '小红书'
  };

  const influencerPlatformNames = {
    douyin: '抖音',
    taobao: '淘宝',
    kuaishou: '快手',
    xiaohongshu: '小红书'
  };

  const influencerPlatformIcons = {
    douyin: 'ri-music-line',
    taobao: 'ri-shopping-bag-line',
    kuaishou: 'ri-video-line',
    xiaohongshu: 'ri-book-open-line'
  };

  const handleBrandClick = (brand: Brand) => {
    setSelectedBrand(brand);
    setModalLayout('default');
  };

  const handleInfluencerSectionClick = () => {
    setModalLayout('influencers-first');
  };

  const handlePlatformSectionClick = () => {
    setModalLayout('qr-first');
  };

  const closeModal = () => {
    setSelectedBrand(null);
    setModalLayout('default');
  };

  return (
    <>
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              南滇古茶 · 热卖品牌
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto">
              精选6大热销品牌，覆盖全价位段，满足不同消费群体需求
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group hover:transform hover:scale-105 cursor-pointer"
                onClick={() => handleBrandClick(brand)}
              >
                {/* Brand Header */}
                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center space-x-4 mb-4">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                    />
                    <div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-800">{brand.name}</h3>
                      <p className="text-sm md:text-base text-blue-600 font-medium">{brand.positioning}</p>
                    </div>
                  </div>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{brand.description}</p>
                </div>

                {/* Brand Content */}
                <div className="p-6">
                  {/* Price Range */}
                  <div className="mb-4">
                    <span className="text-lg md:text-xl font-bold text-blue-600">{brand.priceRange}</span>
                  </div>

                  {/* Core Features */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {brand.coreFeatures.map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-xs md:text-sm rounded-full font-medium"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Sales & Rating */}
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm md:text-base text-gray-600">销量</span>
                      <span className="text-sm md:text-base font-semibold text-gray-800">{brand.sales}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm md:text-base text-gray-600">评分</span>
                      <div className="flex items-center space-x-1">
                        <span className="text-sm md:text-base font-semibold text-yellow-600">{brand.rating}</span>
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`ri-star-${i < Math.floor(brand.rating) ? 'fill' : 'line'} text-xs`}></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">截止 {brand.salesDate}</div>
                  </div>

                  {/* Platform Icons */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 mb-4">
                    <span className="text-sm text-gray-600">点击查看店铺</span>
                    <div className="flex space-x-3">
                      {Object.keys(brand.platforms).map((platform) => (
                        <div
                          key={platform}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-blue-100 transition-colors cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBrandClick(brand);
                            setTimeout(() => handlePlatformSectionClick(), 100);
                          }}
                        >
                          <i className={`${platformIcons[platform as keyof typeof platformIcons]} text-gray-600 text-sm`}></i>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Influencers Section */}
                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">带货达人推荐</h4>
                    <div 
                      className="grid grid-cols-2 gap-3 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBrandClick(brand);
                        setTimeout(() => handleInfluencerSectionClick(), 100);
                      }}
                    >
                      {Object.entries(brand.influencers).slice(0, 4).map(([platform, influencer]) => (
                        <div key={platform} className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2 hover:bg-blue-50 transition-colors">
                          <img
                            src={influencer.avatar}
                            alt={influencer.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-800 truncate">{influencer.name}</div>
                            <div className="flex items-center space-x-1">
                              <i className={`${influencerPlatformIcons[platform as keyof typeof influencerPlatformIcons]} text-xs text-blue-600`}></i>
                              <span className="text-xs text-gray-600">{influencer.followers.split('万')[0]}万</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedBrand && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50" onClick={closeModal}>
          <div 
            className="bg-white rounded-t-3xl md:rounded-xl max-w-6xl w-full h-[90vh] md:h-auto md:max-h-[90vh] flex flex-col touch-pan-y" 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <style jsx>{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            
            {/* 移动端拖拽指示器 */}
            <div className="md:hidden flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>
            
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <img
                    src={selectedBrand.logo}
                    alt={selectedBrand.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{selectedBrand.name}</h3>
                    <p className="text-blue-600 font-medium">{selectedBrand.positioning}</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="hidden md:flex w-8 h-8 items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Tab buttons */}
            <div className="px-6 pt-6 pb-2 flex-shrink-0">
              <div className="flex space-x-4">
                <button
                  onClick={handlePlatformSectionClick}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    modalLayout === 'qr-first' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className="ri-qr-code-line mr-2"></i>
                  店铺二维码
                </button>
                <button
                  onClick={handleInfluencerSectionClick}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    modalLayout === 'influencers-first' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className="ri-user-star-line mr-2"></i>
                  带货达人
                </button>
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <style jsx>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              
              {modalLayout === 'influencers-first' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* 带货达人详情 - 上方 */}
                  <div className="order-1">
                    <h4 className="text-xl font-bold text-gray-800 mb-4">合作带货达人</h4>
                    <div className="space-y-4">
                      {Object.entries(selectedBrand.influencers).map(([platform, influencer]) => (
                        <div key={platform} className="flex items-center space-x-4 bg-gray-50 rounded-lg p-4">
                          <img
                            src={influencer.avatar}
                            alt={influencer.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h5 className="font-semibold text-gray-800">{influencer.name}</h5>
                              <div className="flex items-center space-x-1">
                                <i className={`${influencerPlatformIcons[platform as keyof typeof influencerPlatformIcons]} text-sm text-blue-600`}></i>
                                <span className="text-sm text-blue-600 font-medium">
                                  {influencerPlatformNames[platform as keyof typeof influencerPlatformNames]}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>{influencer.followers}</span>
                              <span className="text-green-600 font-medium">{influencer.sales}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 店铺二维码 - 下方 */}
                  <div className="order-2">
                    <h4 className="text-xl font-bold text-gray-800 mb-4">各平台店铺二维码</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(selectedBrand.platforms).map(([platform, data]) => (
                        <div key={platform} className="text-center">
                          <div className="bg-gray-50 p-4 rounded-lg mb-3">
                            <img
                              src={data.qrCode}
                              alt={`${platformNames[platform as keyof typeof platformNames]}二维码`}
                              className="w-20 h-20 mx-auto object-cover rounded"
                            />
                          </div>
                          <div className="space-y-1">
                            <div className="font-semibold text-gray-800 text-sm">
                              {platformNames[platform as keyof typeof platformNames]}
                            </div>
                            {'rating' in data && (
                              <div className="text-xs text-green-600">好评率 {data.rating}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : modalLayout === 'qr-first' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* 店铺二维码 - 上方 */}
                  <div className="order-1">
                    <h4 className="text-xl font-bold text-gray-800 mb-4">各平台店铺二维码</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(selectedBrand.platforms).map(([platform, data]) => (
                        <div key={platform} className="text-center">
                          <div className="bg-gray-50 p-4 rounded-lg mb-3">
                            <img
                              src={data.qrCode}
                              alt={`${platformNames[platform as keyof typeof platformNames]}二维码`}
                              className="w-20 h-20 mx-auto object-cover rounded"
                            />
                          </div>
                          <div className="space-y-1">
                            <div className="font-semibold text-gray-800 text-sm">
                              {platformNames[platform as keyof typeof platformNames]}
                            </div>
                            {'rating' in data && (
                              <div className="text-xs text-green-600">好评率 {data.rating}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 带货达人详情 - 下方 */}
                  <div className="order-2">
                    <h4 className="text-xl font-bold text-gray-800 mb-4">合作带货达人</h4>
                    <div className="space-y-4">
                      {Object.entries(selectedBrand.influencers).map(([platform, influencer]) => (
                        <div key={platform} className="flex items-center space-x-4 bg-gray-50 rounded-lg p-4">
                          <img
                            src={influencer.avatar}
                            alt={influencer.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h5 className="font-semibold text-gray-800">{influencer.name}</h5>
                              <div className="flex items-center space-x-1">
                                <i className={`${influencerPlatformIcons[platform as keyof typeof influencerPlatformIcons]} text-sm text-blue-600`}></i>
                                <span className="text-sm text-blue-600 font-medium">
                                  {influencerPlatformNames[platform as keyof typeof influencerPlatformNames]}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>{influencer.followers}</span>
                              <span className="text-green-600 font-medium">{influencer.sales}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* 店铺二维码 */}
                  <div>
                    <h4 className="text-xl font-bold text-gray-800 mb-4">各平台店铺二维码</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(selectedBrand.platforms).map(([platform, data]) => (
                        <div key={platform} className="text-center">
                          <div className="bg-gray-50 p-4 rounded-lg mb-3">
                            <img
                              src={data.qrCode}
                              alt={`${platformNames[platform as keyof typeof platformNames]}二维码`}
                              className="w-20 h-20 mx-auto object-cover rounded"
                            />
                          </div>
                          <div className="space-y-1">
                            <div className="font-semibold text-gray-800 text-sm">
                              {platformNames[platform as keyof typeof platformNames]}
                            </div>
                            {'rating' in data && (
                              <div className="text-xs text-green-600">好评率 {data.rating}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 带货达人详情 */}
                  <div>
                    <h4 className="text-xl font-bold text-gray-800 mb-4">合作带货达人</h4>
                    <div className="space-y-4">
                      {Object.entries(selectedBrand.influencers).map(([platform, influencer]) => (
                        <div key={platform} className="flex items-center space-x-4 bg-gray-50 rounded-lg p-4">
                          <img
                            src={influencer.avatar}
                            alt={influencer.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h5 className="font-semibold text-gray-800">{influencer.name}</h5>
                              <div className="flex items-center space-x-1">
                                <i className={`${influencerPlatformIcons[platform as keyof typeof influencerPlatformIcons]} text-sm text-blue-600`}></i>
                                <span className="text-sm text-blue-600 font-medium">
                                  {influencerPlatformNames[platform as keyof typeof influencerPlatformNames]}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>{influencer.followers}</span>
                              <span className="text-green-600 font-medium">{influencer.sales}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* 移动端底部关闭按钮 */}
            <div className="md:hidden flex-shrink-0 p-6 pt-4 border-t border-gray-100">
              <button
                onClick={closeModal}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-4 rounded-full font-medium transition-colors"
              >
                <i className="ri-close-line mr-2"></i>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
